import { useCallback } from 'react'
import { ExternalLink, Palette, Settings2 } from 'lucide-react'
import { useToast } from '../admin/primitives.js'
import { useMe } from '../shell/AppShell.js'
import { entreesLien, useMenuContextuel, type DeclencheursComposes, type GroupeMenu } from '../shell/menu-contextuel.js'
import { useGroupeFavori } from '../shell/menu-favori.js'
import {
  CHEMIN_APPARENCE, ECRAN_ETROIT, cheminServiceInfra, estCheminInterne, exclusionNative, lectureSelection,
  liensCarteAccueil, sauf, type CarteAccueil, type Exclusion,
} from './menu-tuiles-modele.js'

/**
 * MENU CLIC DROIT — TUILES ET CARTES (lot 2, 24/10/2026).
 *
 * Les menus des surfaces en tuiles, hors cockpit : carte de l'accueil « / »,
 * service du hub Infrastructure, tuile de module d'un hub. Navigation et
 * copie SEULEMENT, par les liens que la carte porte déjà. Le menu d'une tuile
 * de cockpit vit dans LayoutGrid, où sont sa fenêtre et son brouillon ; celui
 * d'une section de la page Soirées, dans SectionOrder.
 *
 * Chaque page qu'une carte ouvre s'ajoute aux favoris (lot 6), par l'épingle
 * de l'étoile (useGroupeFavori) : c'est la seule écriture. Pas sur une tuile
 * de cockpit ni sur une section : ce ne sont pas des pages.
 *
 * Ce que le menu propose est calculé dans `menu-tuiles-modele.ts`.
 */

/* ─── là où le menu du navigateur reste ─── */

/** Le point (x, y) de l'écran est-il sur un texte sélectionné, maintenant ? */
function pointSurSelection(x: number, y: number): boolean {
  if (typeof window === 'undefined') return false
  const s = window.getSelection()
  if (!s || s.isCollapsed || s.toString().trim() === '') return false
  for (let i = 0; i < s.rangeCount; i += 1) {
    for (const r of Array.from(s.getRangeAt(i).getClientRects())) {
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return true
    }
  }
  return false
}

/** La sélection retenue à l'appui du bouton droit, une pour toute la page. */
const LECTURE_SELECTION = lectureSelection(pointSurSelection)

/**
 * Où une carte pleine de contenu garde le menu du navigateur (l'ordre et ses
 * raisons : `exclusionNative`, menu-tuiles-modele.ts).
 *
 * Posé sur toute la tuile, le menu prenait aussi le clic droit d'un champ de
 * saisie (Coller), d'un texte sélectionné pour le copier, et tout geste né
 * dans un tiroir ou une confirmation que la tuile monte en portail. Il
 * s'efface là. `poignees` : les poignées de glisser, où le menu ne s'arme
 * jamais (voir `sauf`).
 */
export function gestesNatifs(poignees: string | null = null): Exclusion {
  return exclusionNative(poignees, LECTURE_SELECTION)
}

/** L'écran est-il sous la largeur d'édition (layout.css) ? Lu à l'ouverture du menu. */
export const ecranEtroit = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(ECRAN_ETROIT).matches

const NATIFS = gestesNatifs()

/* ─── tuile de module d'un hub, carte de hub ─── */

/**
 * Le menu d'une tuile qui est un lien de Gestion : « Ouvrir » (ou le libellé
 * de son bouton), nouvel onglet, Copier le lien, et l'ajout aux favoris sous
 * le titre de la tuile. Hors de Gestion, ou sans lien (tuile À VENIR) : rien,
 * le menu du navigateur reste.
 *
 *     const menuLien = useMenuLien()
 *     <Link href={c.route} … {...menuLien(c.route, c.label)}>
 */
export function useMenuLien(): (href: string | null | undefined, titre: string, ouvrir?: string) => DeclencheursComposes {
  const menu = useMenuContextuel()
  const toast = useToast()
  const favori = useGroupeFavori()
  return useCallback((href: string | null | undefined, titre: string, ouvrir?: string) => {
    if (!estCheminInterne(href)) return {}
    return menu(() => [{ actions: entreesLien(href, toast, { ouvrir }) }, favori(href, titre)], titre)
  }, [menu, toast, favori])
}

/* ─── carte de l'accueil « / » ─── */

/**
 * Le menu d'une carte de l'accueil : ses boutons, et Apparence pour les deux
 * cartes qu'une préférence y masque (`liensCarteAccueil`). La préférence
 * s'enregistre sur sa page, avec sa barre d'enregistrement : le menu n'écrit
 * rien lui-même.
 */
export function useMenuCarteAccueil(): (c: CarteAccueil, titre: string) => DeclencheursComposes {
  const menu = useMenuContextuel()
  const toast = useToast()
  const favori = useGroupeFavori()
  return useCallback((c: CarteAccueil, titre: string) => sauf(menu(() => {
    const l = liensCarteAccueil(c)
    const groupes: GroupeMenu[] = []
    if (l.ouvrir) {
      groupes.push({ actions: entreesLien(l.ouvrir.href, toast, { ouvrir: l.ouvrir.libelle }) })
      // « Ce soir » sans soirée ouvre la liste : le favori prend le nom de la page, pas celui de la carte.
      groupes.push(favori(l.ouvrir.href, c.sorte === 'ce-soir' && c.soireeId === null ? 'Soirées' : titre))
    }
    if (l.service) {
      const url = l.service
      // Le bouton de la carte : target="_blank" rel="noopener noreferrer".
      groupes.push({ actions: [{
        id: 'service', libelle: 'Ouvrir le service', icone: <ExternalLink size={15} />,
        agir: () => { window.open(url, '_blank', 'noopener,noreferrer') },
      }] })
    }
    if (l.apparence) {
      groupes.push({ actions: [{ id: 'apparence', libelle: 'Réglages d’Apparence', icone: <Palette size={15} />, href: CHEMIN_APPARENCE }] })
    }
    return groupes
  }, titre), NATIFS), [menu, toast, favori])
}

/* ─── service du hub Infrastructure ─── */

/**
 * Le menu d'un service propre à Gestion (carte principale ou ligne) : sa
 * fiche, et « Personnaliser » pour qui voit déjà ce bouton en tête du hub
 * (infrastructure.catalog.manage), vers la même page.
 */
export function useMenuServiceInfra(): (slug: string, nom: string) => DeclencheursComposes {
  const menu = useMenuContextuel()
  const toast = useToast()
  const me = useMe()
  // Le droit du bouton « Personnaliser », lu de la même façon (InfraCatalog.tsx).
  const perms = me?.permissions ?? null
  const canManage = perms?.includes('infrastructure.catalog.manage') ?? false
  const favori = useGroupeFavori()
  return useCallback((slug: string, nom: string) => menu(() => {
    const groupes: GroupeMenu[] = [{ actions: entreesLien(cheminServiceInfra(slug), toast, { ouvrir: 'Ouvrir la fiche' }) }]
    groupes.push(favori(cheminServiceInfra(slug), nom))
    if (canManage) {
      groupes.push({ actions: [{ id: 'personnaliser', libelle: 'Personnaliser', icone: <Settings2 size={15} />, href: '/settings/services' }] })
    }
    return groupes
  }, nom), [menu, toast, canManage, favori])
}
